import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ErrorMessage, Formik } from "formik";
import { useMutation } from "@tanstack/react-query";
import { OtpInput } from "reactjs-otp-input";
import { useTimer } from "use-timer";
import * as Yup from "yup";

import { sendOtp, verifyOtp } from "src/api/auth";
import { Button } from "src/components/ui/button";
import { useAuth } from "src/providers/AuthProvider";
import { setToLocalStorage, showSuccessToast } from "src/lib/utils";

const OTP_LENGTH = 6;
const RESEND_SECONDS = 60;

const validationSchema = Yup.object().shape({
	email: Yup.string().email("Enter a valid email").required("Email is required"),
	otp: Yup.string().length(OTP_LENGTH, `OTP must be ${OTP_LENGTH} characters long`).required("OTP is required"),
});

interface OtpFormValues {
	email: string;
	otp: string;
}

/** Signup step 2: confirm the emailed code, then sign the user straight in. */
const OTPForm = () => {
	const { setUser } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();

	const { time, reset, start, status } = useTimer({
		initialTime: RESEND_SECONDS,
		endTime: 0,
		timerType: "DECREMENTAL",
		autostart: true,
	});

	const email = (location.state as { email?: string } | null)?.email ?? "";

	useEffect(() => {
		if (!email) navigate("/signup");
	}, [email, navigate]);

	const { mutate, isLoading } = useMutation(verifyOtp, {
		onSuccess: ({ data, message }) => {
			showSuccessToast(message ?? "");

			setUser(data?.user ?? null);
			setToLocalStorage("access_token", data?.access_token);

			navigate("/dashboard");
		},
	});

	const { mutate: sendOTP, isLoading: isSendingOTP } = useMutation(sendOtp, {
		onSuccess: ({ message }) => {
			showSuccessToast(message ?? "");
			reset();
			start();
		},
	});

	return (
		<Formik initialValues={{ email, otp: "" } as OtpFormValues} validationSchema={validationSchema} onSubmit={(values) => mutate(values)}>
			{({ values, setFieldValue, handleSubmit }) => (
				<form onSubmit={handleSubmit} className="tw-grid tw-max-w-[450px] tw-gap-4">
					<div>
						<OtpInput
							value={values.otp}
							onChange={(value: string) => setFieldValue("otp", value)}
							numInputs={OTP_LENGTH}
							shouldAutoFocus
							containerStyle={{ display: "grid", gap: 10, gridTemplateColumns: `repeat(${OTP_LENGTH},1fr)` }}
							inputStyle={{
								paddingInline: 10,
								paddingBlock: 15,
								border: "1px solid #E4E8F0",
								fontSize: "16px",
								width: "100%",
								borderRadius: 10,
							}}
							focusStyle={{ borderColor: "#149C86" }}
						/>
						<ErrorMessage name="otp" component="div" className="tw-mt-1 tw-block tw-text-xs tw-text-red" />
					</div>

					<Button type="submit" variant="secondary" className="tw-w-full" disabled={isLoading}>
						{isLoading ? "Verifying..." : "Continue"}
					</Button>

					<p className="tw-text-[13.5px] tw-text-text-soft">
						Didn't receive the code? You can resend it{" "}
						{status === "RUNNING" ? (
							<span className="tw-font-semibold tw-text-text">in {time}s</span>
						) : (
							<button
								type="button"
								className="tw-font-semibold tw-text-teal hover:tw-underline"
								disabled={isSendingOTP}
								onClick={() => sendOTP({ email })}
							>
								now
							</button>
						)}
					</p>
				</form>
			)}
		</Formik>
	);
};

export default OTPForm;
